import "../../assets/css/coupons.scss";
import Header from "../../components/header";
import Footer from "../../components/footer";
import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useBlocker, useNavigate } from "react-router-dom";
import plusFillSvg from "../../assets/images/icons/plus_fill.svg";
import { getCouponList, registerCoupon } from "../../api/coupon";
import { SessionStorageUtil } from "../../utils/session_storage_util";

// ---------------------------------------------------------------------------
// ConfirmModal
// Promise를 반환하는 명령형 confirm 모달
// resolveRef.current(true/false) 를 호출하면 await confirm()이 반환됨
// ---------------------------------------------------------------------------
function ConfirmModal({ message, resolveRef, onClose }) {
  const handleConfirm = () => {
    resolveRef.current?.(true);
    onClose();
  };
  const handleCancel = () => {
    resolveRef.current?.(false);
    onClose();
  };

  return createPortal(
    <div className="confirm-overlay" onClick={handleCancel}>
      <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
        <p className="confirm-modal-message">
          {message.split("\n").map((line, i) => (
            <span key={i}>
              {line}
              {i < message.split("\n").length - 1 && <br />}
            </span>
          ))}
        </p>
        <div className="confirm-modal-actions">
          <button className="confirm-btn-cancel" onClick={handleCancel}>
            아니오
          </button>
          <button className="confirm-btn-submit" onClick={handleConfirm}>
            예
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ---------------------------------------------------------------------------
// useConfirm hook
// const confirm = useConfirm();
// const result = await confirm("메시지"); // true | false
// ---------------------------------------------------------------------------
function useConfirm() {
  const [modalState, setModalState] = useState(null); // { message }
  const resolveRef = useRef(null);

  const confirm = useCallback((message) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setModalState({ message });
    });
  }, []);

  const handleClose = useCallback(() => {
    setModalState(null);
    resolveRef.current = null;
  }, []);

  const modal = modalState ? (
    <ConfirmModal
      message={modalState.message}
      resolveRef={resolveRef}
      onClose={handleClose}
    />
  ) : null;

  return { confirm, modal };
}

// ---------------------------------------------------------------------------
// 구독 체크 헬퍼
// Dart isSubscribe() / expiredSubtract() 와 동일한 로직
//
// canSubscribe === false  → 현재 구독 없음 (또는 만료)  → isUsed='Y' (즉시 사용)
// canSubscribe === true   → 구독 유지 중               → isUsed='R' (등록만)
// ---------------------------------------------------------------------------
function isSubscriptionActive(subscription, subscriptionExpiredDate) {
  if (subscription !== "0003") {
    return false;
  }

  if (!subscriptionExpiredDate) {
    return false;
  }

  try {
    const now = new Date();
    const expiredDate = new Date(subscriptionExpiredDate);

    if (isNaN(expiredDate.getTime())) {
      return false;
    }

    return expiredDate > now;
  } catch (error) {
    console.error("Date parsing error:", error);
    return false;
  }
}

function checkSubscription() {
  try {
    const userInfo = JSON.parse(SessionStorageUtil.getItem("userInfo") || "{}");
    const subscription = userInfo.subscription ?? null;

    const receipt = userInfo.receipt;
    const subscriptionExpiredDate =
      Array.isArray(receipt) && receipt.length > 0
        ? receipt[0]?.subscriptionExpiredDate ?? ""
        : userInfo.subscriptionExpiredAt ?? "";

    return isSubscriptionActive(subscription, subscriptionExpiredDate);
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// 날짜 유틸
// ---------------------------------------------------------------------------
function isDateExpired(dateStr) {
  if (!dateStr || dateStr === "-") return false;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const target = new Date(dateStr.replaceAll(".", "-"));
  return !isNaN(target) && target <= today;
}

function formatDate(dateStr) {
  if (!dateStr) return "-";
  try {
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}.${m}.${day}`;
  } catch {
    return dateStr;
  }
}

// ---------------------------------------------------------------------------
// 쿠폰 상태 배지
// ---------------------------------------------------------------------------
function CouponStatusBadge({ status, isExpired }) {
  let label, className;
  if (status === "R") {
    if (isExpired) {
      label = "기간 만료";
      className = "badge-expired";
    } else {
      label = "사용 가능";
      className = "badge-available";
    }
  } else {
    label = "사용 완료";
    className = "badge-gift";
  }
  return <span className={`coupon-badge ${className}`}>{label}</span>;
}

// ---------------------------------------------------------------------------
// CouponBoxPage
// ---------------------------------------------------------------------------
const PAGE_SIZE = 12;

function CouponBoxPage() {
  const navigate = useNavigate();
  const { confirm, modal } = useConfirm();

  const [couponInput, setCouponInput] = useState("");
  const [inputError, setInputError] = useState(false);
  const [inputErrorMsg, setInputErrorMsg] =
    useState("올바르지 않은 쿠폰 번호입니다.");

  const [coupons, setCoupons] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(0);
  const [isFetching, setIsFetching] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);

  // ---------------------------------------------------------------------------
  // 목록 조회
  // ---------------------------------------------------------------------------
  const fetchCoupons = useCallback(
    async (page = 0) => {
      setIsFetching(true);
      try {
        const res = await getCouponList(page, PAGE_SIZE);

        if (!res || Object.keys(res).length === 0) {
          SessionStorageUtil.clearAll();
          navigate("/login", { replace: true });
          return;
        }

        if (res.success) {
          setCoupons(res.data || []);
          setTotalPages(res.totalPages || 1);
          setCurrentPage(page);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setIsFetching(false);
      }
    },
    [navigate]
  );

  useEffect(() => {
    fetchCoupons(0);
  }, [fetchCoupons]);

  // ---------------------------------------------------------------------------
  // 실제 API 호출
  // ---------------------------------------------------------------------------
  const executeRegister = async (serialNumber, isUsed, afterPage = 0) => {
    const res = await registerCoupon(serialNumber, isUsed);

    if (res.success) {
      alert(
        res.data?.message ||
          (isUsed === "Y" ? "쿠폰이 사용되었습니다." : "쿠폰이 등록되었습니다.")
      );
      setCouponInput("");
      await fetchCoupons(afterPage);
    } else {
      setInputError(true);
      setInputErrorMsg(res.error?.message || "쿠폰 등록에 실패했습니다.");
    }
  };

  // ---------------------------------------------------------------------------
  // 쿠폰 등록 — Dart _submit() 로직 재현
  //   canSubscribe === false → 확인 모달 → 확인 시 isUsed='Y'
  //   canSubscribe === true  → 바로        isUsed='R'
  // ---------------------------------------------------------------------------
  const handleRegister = async () => {
    const cleaned = couponInput
      .replace(/-/g, "")
      .replace(/\s/g, "")
      .toUpperCase();

    if (cleaned.length < 11) {
      setInputError(true);
      setInputErrorMsg("올바르지 않은 쿠폰 번호입니다.");
      return;
    }

    setInputError(false);
    setIsRegistering(true);

    try {
      // const canSubscribe = checkSubscription();

      // if (!canSubscribe) {
      //   // 구독 없음 → 모달로 즉시 사용 여부 확인 → await로 결과 수신
      //   const confirmed = window.confirm(
      //     "등록하신 쿠폰을 바로 사용하시겠습니까?"
      //   );

      //   if (!confirmed) return;

      //   await executeRegister(cleaned, "Y", 0);
      // } else {
      // 구독 중 → 다이얼로그 없이 등록만
      await executeRegister(cleaned, "R", 0);
      // }
    } catch (err) {
      console.error(err);
      setInputError(true);
      setInputErrorMsg("오류가 발생했습니다. 잠시 후 다시 시도해주세요.");
    } finally {
      setIsRegistering(false);
    }
  };

  // ---------------------------------------------------------------------------
  // 쿠폰함 목록 '사용하기' — Dart widgetItem onTap 재현
  // ---------------------------------------------------------------------------
  const handleUseCoupon = async (coupon) => {
    if (isDateExpired(coupon.couponUseLimitDate)) {
      alert("만료된 쿠폰입니다.");
      return;
    }

    if (coupon.couponStatus !== "R") {
      alert("이미 사용된 쿠폰입니다.");
      return;
    }

    if (checkSubscription()) {
      alert("이미 구독 중입니다. 구독 기간이 만료된 후 사용할 수 있습니다.");
      return;
    }

    const confirmed = window.confirm(
      `쿠폰을 사용하시겠습니까?\n${coupon.couponNumber}`
    );

    if (!confirmed) return;

    try {
      const res = await registerCoupon(coupon.couponNumber, "Y");
      if (res.success) {
        alert(res.data?.message || "쿠폰이 사용되었습니다.");
        await fetchCoupons(currentPage);
      } else {
        alert(res.error?.message || "쿠폰 사용에 실패했습니다.");
      }
    } catch (err) {
      console.error(err);
      alert("오류가 발생했습니다.");
    }
  };

  const handlePageChange = (page) => {
    if (page < 0 || page >= totalPages) return;
    fetchCoupons(page);
  };

  // ---------------------------------------------------------------------------
  // 렌더
  // ---------------------------------------------------------------------------
  return (
    <div className="coupon-box-container">
      {/* Promise 기반 confirm 모달 — document.body에 portal로 렌더 */}
      {modal}

      <div style={{ borderBottom: "1px solid #0D6CF914" }}>
        <Header />
      </div>

      {/* 쿠폰 등록 섹션 */}
      <section className="coupon-register-section">
        <h2 className="coupon-main-title">쿠폰 등록</h2>

        <div className="coupon-register-card">
          <span className="coupon-type-badge">구독권</span>
          <h3 className="coupon-register-prompt">쿠폰 번호를 입력하세요.</h3>

          <div className={`coupon-input-wrap ${inputError ? "error" : ""}`}>
            <input
              type="text"
              className="coupon-input"
              placeholder="11자리 이상 쿠폰 번호"
              value={couponInput}
              onChange={(e) => {
                setCouponInput(e.target.value);
                if (inputError) setInputError(false);
              }}
              maxLength={20}
              disabled={isRegistering}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleRegister();
              }}
            />
          </div>

          <div className="divider"></div>

          <p className={`coupon-input-hint ${inputError ? "error" : ""}`}>
            {inputError
              ? inputErrorMsg
              : "✓ 사각형 번호 혹은 일련번호 11자리 이상"}
          </p>

          <button
            className="coupon-register-btn"
            onClick={handleRegister}
            disabled={isRegistering}
          >
            <span className="btn-icon">
              <img src={plusFillSvg} alt="" />
            </span>{" "}
            {isRegistering ? "등록 중..." : "등록하기"}
          </button>
        </div>
      </section>

      {/* 내 쿠폰함 섹션 */}
      <section className="my-coupon-section">
        <h2 className="coupon-main-title">내 쿠폰함</h2>

        {isFetching ? (
          <div
            style={{ textAlign: "center", padding: "40px 0", color: "#888" }}
          >
            불러오는 중...
          </div>
        ) : coupons.length === 0 ? (
          <div
            style={{ textAlign: "center", padding: "40px 0", color: "#aaa" }}
          >
            보유한 쿠폰이 없습니다.
          </div>
        ) : (
          <div className="coupon-list">
            {coupons.map((coupon) => {
              const expired = isDateExpired(coupon.couponUseLimitDate);
              const isAvailable = coupon.couponStatus === "R" && !expired;

              return (
                <div className="coupon-item" key={coupon.couponsSeq}>
                  <div className="coupon-item-info">
                    <CouponStatusBadge
                      status={coupon.couponStatus}
                      isExpired={expired}
                    />
                    <h3 className="coupon-item-title">
                      {coupon.couponProductName || coupon.couponName || "-"}
                    </h3>
                    <dl className="coupon-item-details">
                      <div className="coupon-detail-row">
                        <dt>쿠폰번호</dt>
                        <dd>{coupon.couponNumber || "-"}</dd>
                      </div>
                      <div className="coupon-detail-row">
                        <dt>등록일자</dt>
                        <dd>{formatDate(coupon.couponRegDate)}</dd>
                      </div>
                      <div className="coupon-detail-row">
                        <dt>만료일자</dt>
                        <dd>{coupon.couponUseLimitDate || "-"}</dd>
                      </div>
                    </dl>
                  </div>
                  <div className="coupon-item-action">
                    <button
                      className={`coupon-use-btn ${
                        !isAvailable ? "disabled" : ""
                      }`}
                      disabled={!isAvailable}
                      onClick={() => isAvailable && handleUseCoupon(coupon)}
                    >
                      사용하기
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {totalPages > 1 && (
          <div className="coupon-pagination">
            <button
              className="page-btn page-nav"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 0}
            >
              이전
            </button>
            {Array.from({ length: totalPages }, (_, i) => i).map((page) => (
              <button
                key={page}
                className={`page-btn ${currentPage === page ? "active" : ""}`}
                onClick={() => handlePageChange(page)}
              >
                {page + 1}
              </button>
            ))}
            <button
              className="page-btn page-nav"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages - 1}
            >
              다음
            </button>
          </div>
        )}
      </section>

      {/* 이용 유의사항 */}
      <section className="terms-section">
        <div className="terms-container">
          <div className="terms-card">
            <h2 className="terms-title">서비스 이용 유의사항</h2>
            <ul className="terms-list">
              <li>쿠폰 등록하기 즉시 내 쿠폰함에 보관됩니다. (MY → 쿠폰함)</li>
              <li>
                구입하신 구독권은 결제내역 페이지에서 확인할 수 있습니다.{" "}
                <a href="/product_step1" className="terms-link">
                  결제내역 바로가기
                </a>
              </li>
              <li>
                기부내역은 기부내역 페이지에서 확인할 수 있습니다.{" "}
                <a href="/donation_step1" className="terms-link">
                  기부내역 바로가기
                </a>
              </li>
              <li>
                유료 구독 서비스 특성상, 이미 제공된 콘텐츠에 대해서는 환불이
                제한될 수 있습니다.
                <br />
                환불은 고객의 신청일 기준 이용 여부 및 남은 기간을 고려하여
                합리적으로 산정됩니다.
                <br />
                결제일 기준 초기 일정 기간내 환불요청이 있을 경우, 콘텐츠 미이용
                시 전액 환불을 원칙으로 합니다.
                <br />
                환불시 평가기준 월 단위 사용료를 차감한 후 잔여 금액을
                반환합니다. (할인 금액은 환불 대상에서 제외)
                <br />
                <a href="/application/service" className="terms-link">
                  환불정책 바로가기
                </a>{" "}
                자세한 환불정책을 확인하실 수 있습니다.
              </li>
              <li>
                유료 사용자에게 제공된 기능은 이용 기간만료 후에는 사용할 수
                없습니다.
              </li>
            </ul>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default CouponBoxPage;
